// src/pages/UnsoldTicketAppraisal/modals/AppraisalConfirmModal.tsx
// Mục 22/23, REQUIRE_FIXBUGS.md — Modal Duyệt/Từ chối phiếu thu hồi vé ế (TicketRecall), bản rút
// gọn theo đúng pattern RecallActionConfirmModal.tsx (Confirm/Cancel) — 2 hành động riêng biệt
// (không dùng dropdown chọn quyết định như bản cũ), vì giờ là 2 endpoint khác nhau
// (POST .../approve, POST .../reject). Ghi chú bắt buộc khi Từ chối, tuỳ chọn khi Duyệt.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { AgentMap, getAgentName } from 'helpers/agentLookup';
import {
  TICKET_RECALL_APPRAISAL_COMMENT_MAX_LENGTH,
  TicketRecall,
} from 'pages/TicketRecall/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('unsold_ticket_appraisal');

export type AppraisalActionType = 'approve' | 'reject';

interface AppraisalConfirmModalProps {
  isOpen: boolean;
  action: AppraisalActionType;
  item: TicketRecall | null;
  agentMap: AgentMap;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (comment?: string) => void;
}

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '0';

const AppraisalConfirmModal: React.FC<AppraisalConfirmModalProps> = ({
  isOpen,
  action,
  item,
  agentMap,
  submitting,
  onClose,
  onSubmit,
}) => {
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setComment('');
      setTouched(false);
    }
  }, [isOpen]);

  if (!item) return null;

  const isApprove = action === 'approve';
  const commentRequired = !isApprove;
  const commentInvalid = touched && commentRequired && !comment.trim();

  const handleSubmit = () => {
    if (commentRequired && !comment.trim()) {
      setTouched(true);
      return;
    }
    onSubmit(comment.trim() || undefined);
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        <i
          className={`${isApprove ? 'ri-shield-check-line text-success' : 'ri-close-circle-line text-danger'} me-2`}
        ></i>
        {isApprove ? st('duyet_phieu_thu_hoi_text') : st('tu_choi_phieu_thu_hoi_text')} {item.recallCode}
      </ModalHeader>
      <ModalBody>
        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <tbody>
              <tr>
                <td width="35%" className="fw-semibold bg-light">
                  {st('nguon_thu_hoi_column')}
                </td>
                <td>{item.agentId ? getAgentName(agentMap, item.agentId) : st('kho_tong_text')}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ke_hoach_phat_hanh_column')}</td>
                <td>{item.planCode || item.issuancePlanId}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('tong_so_ve_thu_hoi_text')}</td>
                <td className="fw-semibold">{formatNumber(item.totalQuantity)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {isApprove ? (
          <Alert color="success" className="mt-3 mb-0 py-2">
            <i className="ri-checkbox-circle-line me-1"></i>
            {st('he_thong_se_kiem_tra_lai_message')}
          </Alert>
        ) : (
          <Alert color="warning" className="mt-3 mb-0 py-2">
            <i className="ri-information-line me-1"></i>
            {st('tu_choi_khong_hoan_lai_ton_message')}
          </Alert>
        )}

        <FormGroup className="mt-3 mb-0">
          <Label for="appraisal-comment">
            {isApprove ? st('ghi_chu_khong_bat_buoc_text') : st('ly_do_tu_choi_text')}{' '}
            {commentRequired && <span className="text-danger">*</span>}
          </Label>
          <Input
            type="textarea"
            id="appraisal-comment"
            rows={4}
            maxLength={TICKET_RECALL_APPRAISAL_COMMENT_MAX_LENGTH}
            placeholder={
              commentRequired ? st('bat_buoc_nhap_ly_do_tu_text') : st('ghi_chu_them_neu_co_text')
            }
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            onBlur={() => setTouched(true)}
            invalid={commentInvalid}
          />
          {commentInvalid && (
            <div className="invalid-feedback d-block">{st('bat_buoc_nhap_ly_do_tu_text_2')}</div>
          )}
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" type="button" onClick={onClose} disabled={submitting}>
          {st('dong_button')}
        </Button>
        <Button
          color={isApprove ? 'success' : 'danger'}
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              {st('dang_xu_ly_text')}
            </>
          ) : (
            <>
              <i className={`${isApprove ? 'ri-check-line' : 'ri-close-line'} me-1`}></i>
              {isApprove ? st('duyet_button') : st('tu_choi_button')}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AppraisalConfirmModal;
